import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LogoWordmark } from "./Logo";
import { scrollToId } from "../lib/scroll";

const LINKS = [
  { label: "Services", href: "#services", testid: "nav-link-services" },
  { label: "Readiness", href: "#readiness", testid: "nav-link-readiness" },
  { label: "Founder", href: "#founder", testid: "nav-link-founder" },
  { label: "Insights", href: "#insights", testid: "nav-link-insights" },
  { label: "Why Compliance", href: "#why", testid: "nav-link-why" },
  { label: "Contact", href: "#contact", testid: "nav-link-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 border-b ${
        scrolled
          ? "bg-[#faf9f6]/90 backdrop-blur-xl border-[#e6e1d5]"
          : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <button
          data-testid="nav-brand-button"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
          className="focus:outline-none focus:ring-2 focus:ring-[#a8824f] rounded-md"
          aria-label="ComplyWise Consult home"
        >
          <LogoWordmark dark />
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={l.testid}
              onClick={() => go(l.href)}
              className="text-sm text-[#45506b] hover:text-[#1b2a4e] transition-colors duration-300 tracking-wide focus:outline-none focus:ring-2 focus:ring-[#a8824f] rounded"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta-button"
            onClick={() => go("#contact")}
            className="group flex items-center gap-1.5 text-sm font-medium text-white px-5 py-2.5 rounded-full bg-[#1b2a4e] hover:bg-[#a8824f] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-[#a8824f]"
          >
            Schedule Consultation
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-[#1b2a4e] p-2 focus:outline-none focus:ring-2 focus:ring-[#a8824f] rounded"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#faf9f6]/95 backdrop-blur-xl border-t border-[#e6e1d5]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => go(l.href)}
                  className="text-left text-base text-[#1b2a4e] hover:text-[#a8824f] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-cta-button"
                onClick={() => go("#contact")}
                className="mt-2 flex items-center justify-center gap-2 text-sm font-medium text-white px-5 py-3 rounded-full bg-[#1b2a4e]"
              >
                Schedule Consultation <ArrowUpRight size={15} />
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
